import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot, faBrain, faChartLine, faHeartPulse } from '@fortawesome/free-solid-svg-icons';
import Step1 from './Step1';
import Step2 from './Step2';
import './AnalysisSteps.css';

function AnalysisSteps({ steps, loading, activeStep }) {
    const [nodePositions, setNodePositions] = useState({});
    const [progressLineStyle, setProgressLineStyle] = useState({});
    const stepCardRefs = useRef([]);
    const progressColumnRef = useRef(null);

    useEffect(() => {
        if (progressColumnRef.current) {
            const newPositions = {};
            const columnTop = progressColumnRef.current.getBoundingClientRect().top;
            const columnHeight = progressColumnRef.current.clientHeight;

            const existingCards = stepCardRefs.current.filter(ref => ref);
            const firstCardTop = existingCards.length
                ? existingCards[0].getBoundingClientRect().top - columnTop
                : 0;
            const lastCardBottom = existingCards.length
                ? existingCards[existingCards.length - 1].getBoundingClientRect().bottom - columnTop
                : columnHeight;

            let firstNodeY = null;
            let lastNodeY = null;

            [1, 2, 3, 4].forEach(stepNum => {
                const cardRef = stepCardRefs.current[stepNum - 1];
                if (cardRef) {
                    const rect = cardRef.getBoundingClientRect();
                    const centerY = rect.top + rect.height / 2 - columnTop;
                    newPositions[stepNum] = centerY;
                    if (firstNodeY === null) firstNodeY = centerY;
                    lastNodeY = centerY;
                } else {
                    const availableHeight = lastCardBottom - firstCardTop;
                    newPositions[stepNum] =
                        firstCardTop + (availableHeight * (stepNum - 1)) / 3;
                }
            });

            if (
                JSON.stringify(newPositions) !== JSON.stringify(nodePositions) &&
                Object.keys(newPositions).length
            ) {
                setNodePositions(newPositions);
                if (firstNodeY !== null && lastNodeY !== null) {
                    setProgressLineStyle({
                        top: `${firstNodeY}px`,
                        height: `${lastNodeY - firstNodeY}px`
                    });
                }
            }
        }
    }, [steps, loading, activeStep]);

    const getIconForStep = stepNum => {
        switch (stepNum) {
            case 1:
                return faHeartPulse;
            case 2:
                return faChartLine;
            case 3:
                return faBrain;
            case 4:
                return faRobot;
            default:
                return faRobot;
        }
    };

    const getStepTitle = stepNum => {
        switch (stepNum) {
            case 1:
                return 'Data Availability Summary';
            case 2:
                return 'Individual Metric Analysis';
            case 3:
                return 'Correlation Data';
            case 4:
                return 'Overall Analysis';
            default:
                return `Step ${stepNum}`;
        }
    };

    const getProgressStyles = () => {
        const firstNodePos = nodePositions[1];
        if (firstNodePos === undefined) return { top: '0px', height: '0px' };

        if (!steps.length) {
            if (loading && activeStep === 1) return { top: '0px', height: '5px' };
            return { top: '0px', height: '0px' };
        }

        const lastActiveStep = steps.length;
        const lastActiveNodePos = nodePositions[lastActiveStep];
        if (lastActiveNodePos === undefined) return { top: '0px', height: '0px' };

        if (loading && activeStep) {
            const nextNodePos = nodePositions[activeStep];
            const height =
                nextNodePos !== undefined
                    ? Math.max(0, nextNodePos - firstNodePos)
                    : Math.max(0, lastActiveNodePos - firstNodePos);
            return { top: '0px', height: `${height}px` };
        }

        return {
            top: '0px',
            height: `${Math.max(0, lastActiveNodePos - firstNodePos)}px`
        };
    };

    return (
        <div className="analysis-container">
            <div className="analysis-progress-column" ref={progressColumnRef}>
                <div className="analysis-progress-line" style={progressLineStyle}>
                    <div className="analysis-progress-fill" style={getProgressStyles()} />
                </div>

                {[1, 2, 3, 4].map(stepNum => (
                    <div
                        key={stepNum}
                        className={`analysis-node ${
                            steps.some(s => s.step === stepNum) ? 'active' : ''
                        } ${loading && activeStep === stepNum ? 'loading' : ''}`}
                        style={{ top: `${nodePositions[stepNum] || 0}px` }}
                    >
                        <FontAwesomeIcon icon={getIconForStep(stepNum)} />
                    </div>
                ))}
            </div>

            <div className="analysis-steps-column">
                {[1, 2, 3, 4].map(stepNum => {
                    const completedStep = steps.find(s => s.step === stepNum);
                    const isActive = !!completedStep;
                    const isLoading = loading && activeStep === stepNum;

                    const card = (
                        <div
                            className={`analysis-step-card ${
                                !isActive ? 'inactive' : ''
                            } ${isLoading ? 'loading' : ''}`}
                            ref={el => (stepCardRefs.current[stepNum - 1] = el)}
                        >
                            <div className="analysis-step-header">
                                <div className="analysis-step-title">
                                    {isActive ? completedStep.title : getStepTitle(stepNum)}
                                </div>
                                {isLoading && (
                                    <div className="analysis-step-loading-indicator">
                                        <div className="loading-spinner" />
                                    </div>
                                )}
                            </div>

                            {isActive && completedStep.details && (
                                <div className="analysis-step-details">
                                    {stepNum === 1 ? (
                                        <Step1 data={completedStep.details} />
                                    ) : stepNum === 2 ? (
                                        <Step2 data={completedStep} />
                                    ) : (
                                        completedStep.details.map((detail, i) => (
                                            <div key={i} className="analysis-detail-item">
                                                {detail}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    );

                    return (
                        <React.Fragment key={stepNum}>
                            {card}
                            {stepNum !== 4 && (
  <>
    <div className="analysis-step-separator" />
    <div className="analysis-step-separator-inner" />
  </>
)}

                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

export default AnalysisSteps;
